/**
 * The creation dialog's "Território" strip (Figma node `96:344`, inside `96:2`,
 * `Organização · Nova campanha (TSE)`, file `LrdQopN1jNWKJFWxOkn0IY`): what the platform
 * understood about the picked candidature's electoral scope, confirming to the reader that it
 * read their campaign's coverage correctly.
 *
 * The frame draws this as one row -- an icon, the place name, and its counts joined by a
 * middle dot -- not a `StatCard`/`StatGrid`; that stays the figure treatment here because the
 * frame is the one source of this design, and it already keeps every count out of a sentence.
 *
 * Nothing renders before a candidature carries a TSE identity (`tse_election_id`/
 * `tse_registration_id`; a manually-typed candidate never has one) and nothing renders while the
 * scope is still unsettled -- no skeleton, no zero, no dash, since a transient figure a reader
 * could mistake for the real one is worse than a beat of nothing. A failed fetch is the one state
 * that does render something, so it reads apart from "still loading" -- except a 404, which
 * `read_candidature_scope` returns for the ordinary case of no seeded places for this candidature
 * (no more of a failure than `scope: null` on the record), so it renders nothing too.
 */
import type { ReactNode } from 'react';
import { MapPinned } from 'lucide-react';

import { ErrorState } from '../../../chrome/state';
import { useCandidatureScope } from '../tse/api/queries';
import type { LinkedCandidate } from '../tse/api/schemas';
import { scopeCountLabel } from './localityStripMessages';

export function LocalityStrip(props: { tenantId: string; candidate: LinkedCandidate }): ReactNode {
  const candidate = props.candidate;
  const linked =
    candidate !== null &&
    candidate.tse_election_id !== null &&
    candidate.tse_registration_id !== null
      ? candidate
      : null;
  const scope = useCandidatureScope(
    props.tenantId,
    linked?.tse_election_id ?? null,
    linked?.tse_registration_id ?? null,
  );

  if (linked === null || scope.status === 'pending') return null;
  if (scope.status === 'error') {
    if (scope.error.failure.kind === 'not-found') return null;
    return <ErrorState failure={scope.error.failure} onRetry={() => void scope.refetch()} />;
  }

  return (
    <div className="flex items-center gap-2 rounded-lg bg-surface-brief p-3">
      <MapPinned aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <span className="text-label text-foreground">{scope.data.name}</span>
        <span className="inline-flex flex-wrap items-center gap-1 text-caption text-muted-foreground">
          {scope.data.counts.map((count, index) => (
            <span key={count.level} className="inline-flex items-center gap-1">
              {index > 0 && (
                // eslint-disable-next-line no-restricted-syntax -- decorative separator, same glyph in every locale.
                <span aria-hidden>·</span>
              )}
              <span>{scopeCountLabel(count)}</span>
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
